// this is a direct port of the MinBike java class provided by Schweizmobil
class CycleTime {
  power: number = 0.4688; // Mass specific power [W/kg]
  density: number = 1.1962; // Air density
  cwA: number = 0.55; // Air drag
  mass: number = 90; // Mass of bike and rider
  cr: number = 0.005; // Static roll resistance coefficient
  slopeDependentPower: boolean = true;
  g: number = 9.81;
  minSpeed: number = 3 / 3.6;
  maxSpeed: number = 50 / 3.6;

  setPowerFromHorizontalSpeed(horizontalSpeed: number): void {
    // Computes mass-specific power from horizontal speed
    const v: number = horizontalSpeed * 3.6; // speed in km/h

    if (v < 10) {
      this.cwA = 0.625;
    } else if (v > 35) {
      this.cwA = 0.25;
    } else {
      this.cwA = (-0.375 * (v - 10)) / 25 + 0.625;
    }

    this.power =
      (0.5 * this.cwA * this.density * horizontalSpeed ** 3) / this.mass +
      this.g * this.cr * horizontalSpeed;
  }

  powerApplied(slope: number): number {
    // Sets the power according to slope
    if (slope < -0.03) {
      return 0;
    } else if (slope > 0.05) {
      return 1.3 * this.power;
    } else {
      if (slope < 0) {
        return this.power * (slope / 0.03 + 1);
      } else {
        return this.power * ((slope * 0.3) / 0.05 + 1);
      }
    }
  }

  getSpeed(slope: number): number {
    // Solves the equation f(v) = 0.5 * cW * A * density * v^3 + g * (slope + cr) * v - pS
    const a: number = (0.5 * this.cwA * this.density) / this.mass;
    const c: number = this.g * (slope + this.cr);
    let d: number;

    if (this.slopeDependentPower) {
      d = -this.powerApplied(slope);
    } else {
      d = -this.power;
    }

    const x0: number = 20.0;

    const f = new CubicFunction(a, c, d, x0);
    const v: number = f.solve();

    // Bounding
    if (v < this.minSpeed) {
      return this.minSpeed;
    } else if (v > this.maxSpeed) {
      return this.maxSpeed;
    } else {
      return v;
    }
  }
}

class CubicFunction {
  x0: number = 20.0;
  a: number;
  c: number;
  d: number;

  constructor(a: number, c: number, d: number, x0: number) {
    this.a = a;
    this.c = c;
    this.d = d;
    this.x0 = x0;
  }

  f(x: number): number {
    return x * (this.a * x * x + this.c) + this.d;
  }

  fder(x: number): number {
    return 3 * this.a * x * x + this.c;
  }

  solve(): number {
    // Newton
    const precision: number = 0.001;
    let x: number = this.x0;
    //let i: number = 0;

    while (Math.abs(this.f(x)) > precision) {
      while (Math.abs(this.fder(x)) < 0.01) {
        x += 0.1;
      }
      const xn: number = x - this.f(x) / this.fder(x);
      //i += 1;
      x = xn;
    }

    return x;
  }
}

const bike = new CycleTime();
const speedCache: number[][] = new Array(36);
for (let i = 0; i < 36; i++) {
  speedCache[i] = new Array(2001).fill(0);
}

export function getDiscreteBikingSpeed(
  horizontalSpeedKm: number,
  slope: number,
): number {
  if (slope > 1) {
    slope = 1;
  }
  if (slope < -1) {
    slope = -1;
  }
  if (horizontalSpeedKm < 5 || horizontalSpeedKm > 40) {
    throw new Error(`Invalid horizontal speed: ${horizontalSpeedKm} km/h`);
  }

  const horizontalSpeedKm0: number = Math.round(horizontalSpeedKm); // The interface only allows integers [5-40]
  const slope3: number = Math.round(slope * 1000) / 1000; // Super granularity of 0.1%

  const key1: number = horizontalSpeedKm0 - 5;
  const key2: number = Math.round(slope3 * 1000) + 1000;
  let computedSpeed: number = speedCache[key1][key2];

  if (computedSpeed === 0.0) {
    // Compute discretized speed
    bike.setPowerFromHorizontalSpeed(horizontalSpeedKm0 / 3.6);
    computedSpeed = bike.getSpeed(slope3);
    speedCache[key1][key2] = computedSpeed;
  }

  return computedSpeed;
}
